import { HeurekaElementFactory } from "../util/HeurekaElementFactory";
import { heureka } from "@otto-ec/heureka";
import type { Appendable } from "../util/Appendable";
import { clearFormInput, interceptSubmit } from "../filter/FilterFormActions";
import { dereference, readLabel } from "../tracking/FeatureTracking";
import { isSelected, SvelteFacetValue, updateFacetValueTagPosition } from "../filter/SvelteFacetValue";
import { clone, element } from "../util/Utils";
import { setChangeLabels } from "../filter/FilterTracking";
import { SvelteFacet } from "../filter/SvelteFacet";
import { events } from "../events";
import { RefinementBar } from "../refinementBar/RefinementBar";
import { NextBestActionComponent } from "../nextBestAction/NextBestActionComponent";
import type { FilterSectionLoadedEvent } from "../multifiltering/FilterTypes";

const CLASS_NEXT_BEST_ACTION_WRAPPER = "hc_facetValueTags";
const CLASS_DESELECT_ALL = "hc_deselectAll";
const SELECTOR_DESELECT_ALL = `.${CLASS_DESELECT_ALL}`;
const SELECTOR_TEMPLATE_FACET_VALUE_TAGS = `.hc_svelteFacetValueTagsWrapper .${CLASS_NEXT_BEST_ACTION_WRAPPER}`;
const CATEGORY_PATH_FACET_NAME = "kategorien";

export class FacetValueTagsComponent implements Appendable {
  static readonly factory = HeurekaElementFactory.byClass(CLASS_NEXT_BEST_ACTION_WRAPPER, FacetValueTagsComponent);

  /*               */

  constructor(readonly tags: HTMLElement) {}

  /*                  */

  static template(rootElement?: ParentNode | null) {
    return FacetValueTagsComponent.factory.pick(SELECTOR_TEMPLATE_FACET_VALUE_TAGS, rootElement);
  }

  /*               */

  init() {
    this.handleSelectedCategoryPathFacetValues();
    this.registerListener();
    this.deselectAllButton?.addEventListener("click", () => {
      /*                                                                                         */
      this.selectedValues.forEach((tag) => {
        tag.updateTrackingLabelsOnReferenced("deleted", "x_all_top");
      });
    });
    this.selectedValues.forEach((tag) => {
      tag.facetValue.addEventListener("click", () => {
        /*                                                                                   */
        tag.updateTrackingLabelsOnReferenced("deleted", "x_single_top");
      });
    });
    return this;
  }

  static register() {
    if (NextBestActionComponent.isNotVisible()) {
      events.onFilterSectionLoaded(FacetValueTagsComponent.initAll);
    }
  }

  static initAll(event: FilterSectionLoadedEvent, rootElement?: ParentNode) {
    FacetValueTagsComponent.emitSelectedFacetValues(event, rootElement);
  }

  private static emitSelectedFacetValues(event: FilterSectionLoadedEvent, rootElement?: ParentNode) {
    const refinementBar = RefinementBar.facetValueTagsElement(rootElement);
    if (refinementBar) {
      const facetValueTags = FacetValueTagsComponent.factory.pick(undefined, refinementBar);
      const selectedValues = facetValueTags?.selectedValues.map((value) => value.title);
      if (selectedValues && selectedValues.length) {
        events.facetValueTagsLoaded(event.rule, selectedValues);
      }
    }
  }

  /*                       */

  clone() {
    return FacetValueTagsComponent.factory.create(clone(this.tags)).init();
  }

  get deselectAllButton(): HTMLElement | null {
    return element(SELECTOR_DESELECT_ALL, this.tags);
  }

  get values(): SvelteFacetValue[] {
    return SvelteFacetValue.factory.all(this.tags);
  }

  get selectedValues(): SvelteFacetValue[] {
    return this.values.filter(isSelected);
  }

  get deselectAll(): HTMLFormElement | undefined {
    return this.tags.querySelector(SELECTOR_DESELECT_ALL);
  }

  appendTo(elem: HTMLElement): void {
    elem.appendChild(this.tags);
    trackFacetValueTags();
  }

  removeNonSelectedNbaWithRef(ref: string): void {
    const value = this.values.find((value) => value.ref === ref && !value.checked);
    value?.liElem?.remove();
  }

  /*                 */

  private handleChange(event: Event) {
    if (event.target instanceof HTMLElement) {
      const referenced = dereference(event.target);
      const filterForm = referenced.closest("form");
      /*                                                                                           */
      /*                                                   */
      if (filterForm) {
        setChangeLabels(filterForm, { san_FacetLocation: "list_initial" });
        filterForm.dataset.tsFeatureFilterMethod = readLabel(referenced, "san_FilterMethod");
      }
    }

    heureka.filterSection_change.emit({ target: event.target });
  }

  registerListener() {
    this.tags.addEventListener("change", this.handleChange);
    this.deselectAll?.addEventListener("submit", (event) => {
      this.values.forEach((value) => {
        const referenced = dereference(value.facetValue);
        clearFormInput(referenced);
      });
      interceptSubmit(event);
    });
  }

  /*       */

  private handleSelectedCategoryPathFacetValues() {
    const selectedCategoryPathNbaValues = this.values.filter(
      (value) => value.checked && value.name === CATEGORY_PATH_FACET_NAME,
    );
    const selectedCategoryPathNbaValuesIds = selectedCategoryPathNbaValues.map(
      (value) => value.facetValue.dataset.tsFeatureRef,
    );
    const categoryPathNbaTemplate = selectedCategoryPathNbaValues?.[0];
    if (categoryPathNbaTemplate) {
      const categoryPathFacet = SvelteFacet.facetName("kategorien");
      const categoryPathSelectedValues =
        categoryPathFacet?.selectedValues?.filter((value) => {
          return !selectedCategoryPathNbaValuesIds.includes(value.id);
        }) || [];
      const parentNode = categoryPathNbaTemplate.liElem.parentNode;
      categoryPathSelectedValues.forEach((value) => {
        this.removeNonSelectedNbaWithRef(value.id);
        const clonedNbaValue = categoryPathNbaTemplate.cloneWithData(value.id, value.spanInnerHTML);
        const firstSibling = categoryPathNbaTemplate.liElem.nextSibling;
        parentNode.insertBefore(clonedNbaValue.liElem, firstSibling);
      });
    }
  }
}

function trackFacetValueTags() {
  const facetValueTagsTemplate = FacetValueTagsComponent.template();
  if (facetValueTagsTemplate) {
    facetValueTagsTemplate.values.forEach(updateFacetValueTagPosition);
  }
}
